import { memo, useCallback, useMemo } from "react";

import WarningInfoIcon from "@/components/ui/icon/warning-info";

import { useAirHostPricePlanQuery, useAirHostReservationSyncJobQuery } from "../../../model/air-host/repository";
import { useInitialized } from "../../../model/air-host/use-case";
import { ReferenceArticles, SettingsSiteControllerHeader } from "../_shared";
import { REFERENCE_ARTICLES } from "./constants";
import { useSettingsSiteControllerAirHost } from "./hooks";
import { SettingsSiteControllerAirHostBaseInfoIsland } from "./island/base-info";
import { SettingsSiteControllerAirHostReservationSyncJobIsland } from "./island/reservation-sync-job";
import { SettingsSiteControllerAirHostRoomMappingIsland } from "./island/room-mapping";

import type { SiteControllerSettingBaseFormModel } from "../../../model/base/type";
import type { SiteControllerSettingBaseFormModelMethodsType } from "../../../model/base/use-case/use-create-model/methods";
import type { SiteControllerSettingBaseFormModelOnSetViewModelType } from "../../../model/base/use-case/use-create-model/use-create-model";

type Props = {
	viewModel: SiteControllerSettingBaseFormModel[ "ViewModel" ]; //
	onSetViewModel: SiteControllerSettingBaseFormModelOnSetViewModelType;
	resetViewModel: () => void;
	siteControllerSettingBaseFormModelMethodsType: SiteControllerSettingBaseFormModelMethodsType;

	queryData: SiteControllerSettingBaseFormModel[ "QueryData" ];
};

export const SettingsSiteControllerAirHost = memo(
	({
		viewModel: baseViewModel,
		onSetViewModel, //
		resetViewModel,
		siteControllerSettingBaseFormModelMethodsType,
		queryData,
	}: Props) => {
		const { onRefetchAirHostPricePlan, pricePlanData, onSetPricePlanData } = useAirHostPricePlanQuery({
			hotelID: baseViewModel.hotelID,
			airHostPartnerID: queryData.airHostSettingsBaseInfo.airHostPartnerID,
			airHostApiAccessToken: queryData.airHostSettingsBaseInfo.airHostAPIAccessToken,
			airHostPropertyID: queryData.airHostSettingsBaseInfo.airHostPropertyID,
		});

		const { reservationSyncJob, onRefetchReservationSyncJob } = useAirHostReservationSyncJobQuery({
			hotelID: baseViewModel.hotelID,
			airHostPartnerID: queryData.airHostSettingsBaseInfo.airHostPartnerID,
			airHostApiAccessToken: queryData.airHostSettingsBaseInfo.airHostAPIAccessToken,
			airHostPropertyID: queryData.airHostSettingsBaseInfo.airHostPropertyID,
		});

		const { airHostViewModel, ...siteControllerSettingAirHostFormModelMethods } = useInitialized({
			viewModel: baseViewModel,
			queryData,
			onSetViewModel,
		});

		const { onUpdate } = useSettingsSiteControllerAirHost({
			viewModel: airHostViewModel,
			onResetViewModel: resetViewModel,
		});

		const isValid = useMemo(
			() => baseViewModel.isDirty && !baseViewModel.hasError,
			[ baseViewModel.hasError, baseViewModel.isDirty ],
		);

		const hasMappings = useMemo(
			() => queryData.airHostSettingsBaseInfo.chillnnRoomAndAirHostPricePlanMappings.length > 0,
			[ queryData.airHostSettingsBaseInfo.chillnnRoomAndAirHostPricePlanMappings.length ],
		);

		const handleRefetchReservationSyncJob = useCallback(async () => {
			await onRefetchReservationSyncJob({ hotelID: baseViewModel.hotelID });
		}, [ onRefetchReservationSyncJob, baseViewModel.hotelID ]);

		return (
			<>
				<SettingsSiteControllerHeader onUpdate={onUpdate} isValid={isValid} />


				<ReferenceArticles articles={REFERENCE_ARTICLES} />

				{/* ベータ版警告バナー */}
				<div
					role="alert"
					className="mx-4 mt-4 flex items-start gap-3 rounded-lg border border-warning-200 border-l-4 border-l-warning-600 bg-warning-50 p-4 desktop:mx-12 desktop:mt-6"
				>
					<WarningInfoIcon className="mt-0.5 shrink-0 text-warning-600" size="md" />
					<div className="space-y-1">
						<p className="text-sm tracking-wider font-bold text-warning-600">ベータ版の機能です</p>
						<p className="text-sm text-zinc-600">
							この機能は現在ベータ版として提供しております。一部動作が不安定な場合がございます。<br />ご不明点がございましたら、担当者までお気軽にお問い合わせくださいませ。
						</p>
					</div>
				</div>

				<SettingsSiteControllerAirHostBaseInfoIsland
					hotelID={baseViewModel.hotelID}
					baseViewModel={baseViewModel}
					siteControllerSettingBaseFormModelMethodsType={siteControllerSettingBaseFormModelMethodsType}
					airHostViewModel={airHostViewModel}
					siteControllerSettingAirHostFormModelMethods={siteControllerSettingAirHostFormModelMethods}
					queryData={queryData}
				/>

				<SettingsSiteControllerAirHostRoomMappingIsland
					onRefetchAirHostPricePlan={onRefetchAirHostPricePlan}
					baseViewModel={baseViewModel}
					siteControllerSettingBaseFormModelMethodsType={siteControllerSettingBaseFormModelMethodsType}
					airHostViewModel={airHostViewModel}
					siteControllerSettingAirHostFormModelMethods={siteControllerSettingAirHostFormModelMethods}
					queryData={queryData}
					pricePlanData={pricePlanData}
					onSetPricePlanData={onSetPricePlanData}
				/>

				<SettingsSiteControllerAirHostReservationSyncJobIsland
					hotelID={baseViewModel.hotelID}
					reservationSyncJob={reservationSyncJob}
					hasMappings={hasMappings}
					onRefetchReservationSyncJob={handleRefetchReservationSyncJob}
				/>
			</>
		);
	},
);

SettingsSiteControllerAirHost.displayName = "SettingsSiteControllerAirHost";
